import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import ThirdPartyEmojiPicker from "emoji-picker-react";
import "../styles/MessageContextMenu.css";
import MessageInfoModal from "./MessageInfoModal";

const DESKTOP_MENU_MAX_WIDTH = 280;

const MessageContextMenu = ({
  message,
  isVisible,
  position,
  onClose,
  onDelete,
  onCopy,
  onStar,
  onReply,
  onShare,
  onForward,
  isMe,
  onReact,
  currentUser,
  onEdit,
  onViewStatus,
  onPin,
  canPin = true,
  canUnpin = false,
}) => {
  const [showDeleteOptions, setShowDeleteOptions] = useState(false);
  // For direct messages, use starredBy array for per-user starring
  const [isStarred, setIsStarred] = useState(
    message?.starredBy && currentUser?.id
      ? message.starredBy.includes(currentUser.id)
      : message?.isStarred || false
  );
  const [isPinned, setIsPinned] = useState(Boolean(message?.isPinned));
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [messageInfo, setMessageInfo] = useState(null);
  const menuRef = useRef(null);
  const [showEmojiGrid, setShowEmojiGrid] = useState(false);
  const [deskPlacement, setDeskPlacement] = useState(null);
  const isMobile =
    typeof window !== "undefined" ? window.innerWidth <= 768 : false;

  const getReactionUserId = (reaction) =>
    (
      reaction?.userId?._id ||
      reaction?.userId?.id ||
      reaction?.userId
    )?.toString?.() || "";

  const currentUserId = currentUser?.id?.toString?.() || "";
  const myReactionType =
    (message?.reactions || []).find(
      (r) => getReactionUserId(r) === currentUserId
    )?.type || null;

  const handleSelectReaction = (emoji) => {
    if (!onReact || !emoji) return;
    // Re-selecting the same emoji removes the reaction
    const nextType = myReactionType === emoji ? null : emoji;
    onReact(message, nextType);
    setShowEmojiGrid(false);
    onClose();
  };

  // Update isStarred when message changes
  useEffect(() => {
    if (message && currentUser?.id) {
      if (Array.isArray(message.starredBy)) {
        setIsStarred(message.starredBy.includes(currentUser.id));
      } else {
        setIsStarred(message.isStarred || false);
      }
    }
    setIsPinned(Boolean(message?.isPinned));
  }, [message, currentUser]);

  useEffect(() => {
    if (!isVisible) {
      setShowInfoModal(false);
      setMessageInfo(null);
      setShowDeleteOptions(false);
      setShowEmojiGrid(false);
      setDeskPlacement(null);
    }
  }, [isVisible]);

  useLayoutEffect(() => {
    if (isMobile) {
      setDeskPlacement(null);
      return;
    }
    if (!isVisible || typeof window === "undefined") {
      return;
    }
    const margin = 12;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top = position.y;
    let left = Math.min(
      position.x,
      Math.max(margin, vw - DESKTOP_MENU_MAX_WIDTH - margin)
    );
    let maxHeight = Math.max(80, vh - top - margin);

    const el = menuRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      if (rect.bottom > vh - margin) {
        top = Math.max(margin, vh - rect.height - margin);
        maxHeight = Math.max(80, vh - top - margin);
      }
      let l = left;
      if (rect.right > vw - margin) {
        l = Math.max(margin, vw - rect.width - margin);
      }
      if (l < margin) l = margin;
      left = l;
    }

    setDeskPlacement({ top, left, maxHeight });
  }, [
    isVisible,
    isMobile,
    position.x,
    position.y,
    showDeleteOptions,
    showEmojiGrid,
    message?._id,
  ]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isVisible, onClose]);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isVisible, onClose]);

  const handleDeleteClick = () => {
    setShowDeleteOptions(true);
  };

  const handleDeleteForMe = () => {
    if (!message) return;
    onDelete("forMe");
    setShowDeleteOptions(false);
    onClose();
  };

  const handleDeleteForEveryone = () => {
    if (!message) return;
    onDelete("forEveryone");
    setShowDeleteOptions(false);
    onClose();
  };

  const handleCancelDelete = () => {
    setShowDeleteOptions(false);
  };

  const handleCopyClick = () => {
    if (!message) return;
    const textToCopy =
      message.messageType === "voice" ? "Voice message" : message.text;
    navigator.clipboard.writeText(textToCopy).then(() => {
      // Show a brief success message
      const originalText = document.title;
      document.title = "Message copied!";
      setTimeout(() => {
        document.title = originalText;
      }, 1000);
    });
    onClose();
  };

  const handleStarClick = () => {
    if (!message) return;
    setIsStarred(!isStarred);
    onStar(message._id, !isStarred);
    onClose();
  };

  const handlePinClick = () => {
    if (!message || !onPin) return;
    setIsPinned(!isPinned);
    onPin(message._id, !isPinned);
    onClose();
  };

  const handleReplyClick = () => {
    if (!message) return;
    onReply(message);
    onClose();
  };

  const handleEditClick = () => {
    if (!message) return;
    onEdit && onEdit(message);
    onClose();
  };

  const handleForwardClick = () => {
    if (!message) return;
    onForward && onForward(message);
    onClose();
  };

  const handleShareClick = () => {
    if (!message) return;
    const textToShare =
      message.messageType === "voice" ? "Voice message" : message.text;

    if (navigator.share) {
      navigator
        .share({
          title: "Shared message from UcmaApp",
          text: textToShare,
          url: window.location.href,
        })
        .catch(console.error);
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(textToShare);
      const originalText = document.title;
      document.title = "Message copied to clipboard!";
      setTimeout(() => {
        document.title = originalText;
      }, 1000);
    }
    onClose();
  };

  const handleInfoClick = () => {
    if (!message) return;

    // Check if current user has read receipts enabled
    const readReceiptsEnabled =
      currentUser?.privacySettings?.readReceipts !== false;

    // Adjust status display based on read receipts setting
    let displayStatus = message.status;
    if (message.status === "read" && !readReceiptsEnabled) {
      displayStatus = "delivered";
    }

    // Show message info (timestamp, status, etc.)
    const formatParticipant = (participant) => {
      if (!participant) return "N/A";
      if (typeof participant === "string") return participant;
      if (typeof participant === "object") {
        return (
          participant.username ||
          participant.name ||
          participant.phoneNumber ||
          participant.phone ||
          participant._id ||
          participant.id ||
          "Unknown"
        );
      }
      return String(participant);
    };

    const info = {
      id: message._id,
      timestamp: new Date(message.createdAt).toLocaleString(),
      status: displayStatus || "sent",
      messageType: message.messageType || "text",
      from: formatParticipant(message.from),
      to:
        message.groupName ||
        (message.groupId && !message.to
          ? "Group"
          : formatParticipant(message.to)),
      text:
        message.messageType === "voice"
          ? "Voice message"
          : message.text || "(No message text)",
    };

    setMessageInfo(info);
    setShowInfoModal(true);
    setShowDeleteOptions(false);
    setShowEmojiGrid(false);
  };

  const handleStatusClick = async () => {
    if (!message) return;

    if (onViewStatus) {
      try {
        const info = await onViewStatus(message);
        if (info) {
          setMessageInfo(info);
          setShowInfoModal(true);
          setShowDeleteOptions(false);
          setShowEmojiGrid(false);
          return;
        }
      } catch (error) {
        console.error("Error loading message status:", error);
      }
    }

    handleInfoClick();
  };

  if (!isVisible || !message) return null;

  // Group own messages only (Status/Info). No Info row in 1:1 chat (incoming or outgoing).
  const showGroupStatusInfo = Boolean(message?.groupId && isMe && onViewStatus);

  // Call-history entries (both incoming & outgoing) get a minimal menu:
  // only "Delete for me" and "Reply".
  const isCallMessage =
    message?.messageType === "call" || message?.type === "call";

  const margin = 12;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  const desktopMenuStyle = isMobile
    ? null
    : deskPlacement || {
        top: position.y,
        left: Math.min(
          position.x,
          Math.max(margin, vw - DESKTOP_MENU_MAX_WIDTH - margin)
        ),
        maxHeight: Math.max(80, vh - position.y - margin),
      };

  const menuLayer =
    !showInfoModal && typeof document !== "undefined" ? (
      <>
        <div className="message-context-menu-overlay" onClick={onClose} />

        <div
          ref={menuRef}
          className={`message-context-menu${isMobile ? " message-context-menu--mobile" : ""}`}
          style={
            isMobile
              ? {
                  left: 0,
                  right: 0,
                  bottom: 0,
                  top: "auto",
                }
              : {
                  top: desktopMenuStyle.top,
                  left: desktopMenuStyle.left,
                  maxHeight: desktopMenuStyle.maxHeight,
                }
          }
        >
        {isCallMessage ? (
          <>
            <button
              className="context-menu-item delete"
              onClick={handleDeleteForMe}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M3 6h18" />
                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" />
                <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
              </svg>
              Delete for me
            </button>

            <button
              className="context-menu-item reply"
              onClick={handleReplyClick}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="9,11 12,14 22,4" />
                <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
              </svg>
              Reply
            </button>
          </>
        ) : (
        <>
        {/* Quick Reactions Row */}
        <div className="reactions-row">
          {["👍", "👎", "❤️", "😊"].map((e) => (
            <button
              key={e}
              className={`reaction-btn${myReactionType === e ? " active" : ""}`}
              title={myReactionType === e ? "Remove reaction" : "React"}
              onClick={() => handleSelectReaction(e)}
            >
              {e}
            </button>
          ))}
          <button
            className="reaction-btn more"
            title="More"
            onClick={() => setShowEmojiGrid((v) => !v)}
          >
            +
          </button>
        </div>
        {showEmojiGrid && (
          <div className="full-emoji-picker">
            <ThirdPartyEmojiPicker
              onEmojiClick={(emojiData) => {
                handleSelectReaction(emojiData?.emoji);
              }}
              autoFocusSearch={false}
              lazyLoadEmojis
              previewConfig={{ showPreview: false }}
              height={isMobile ? 260 : 320}
            />
          </div>
        )}
        <div className="divider" />
        {showDeleteOptions ? (
          <div className="delete-options">
            <div className="delete-options-header">
              <span>Delete message</span>
            </div>
            <div className="delete-options-content">
              <button
                className="delete-option-btn delete-for-me"
                onClick={handleDeleteForMe}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M3 6h18" />
                  <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" />
                  <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                </svg>
                Delete for me
              </button>

              {isMe && (
                <button
                  className="delete-option-btn delete-for-everyone"
                  onClick={handleDeleteForEveryone}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M3 6h18" />
                    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" />
                    <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    <path d="M10 11l4 4" />
                    <path d="M14 11l-4 4" />
                  </svg>
                  Delete for everyone
                </button>
              )}

              <button
                className="delete-option-btn cancel"
                onClick={handleCancelDelete}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Edit option - only for own text messages */}
            {isMe && message.messageType !== "voice" && message.messageType !== "image" && 
             message.messageType !== "video" && message.messageType !== "audio" && 
             message.messageType !== "document" && message.messageType !== "contact" && 
             message.messageType !== "location" && message.messageType !== "deleted" && (
              <button
                className="context-menu-item edit"
                onClick={handleEditClick}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Edit
              </button>
            )}

            <button
              className="context-menu-item delete"
              onClick={handleDeleteClick}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M3 6h18" />
                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" />
                <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
              </svg>
              Delete
            </button>

            <button
              className="context-menu-item copy"
              onClick={handleCopyClick}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
              </svg>
              Copy
            </button>

            <button
              className={`context-menu-item star ${isStarred ? "starred" : ""}`}
              onClick={handleStarClick}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill={isStarred ? "currentColor" : "none"}
                stroke="currentColor"
                strokeWidth="2"
              >
                <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
              </svg>
              {isStarred ? "Unstar" : "Star"}
            </button>

            {onPin &&
              !message.viewOnce &&
              message.messageType !== "deleted" &&
              ((!isPinned && canPin) || (isPinned && canUnpin)) && (
              <button
                className={`context-menu-item pin ${isPinned ? "pinned" : ""}`}
                onClick={handlePinClick}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill={isPinned ? "currentColor" : "none"}
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 17v5" />
                  <path d="M9 10.76a2 2 0 01-1.11 1.79l-1.78.9A2 2 0 005 15.24V16h14v-.76a2 2 0 00-1.11-1.79l-1.78-.9A2 2 0 0115 10.76V7a1 1 0 011-1 2 2 0 000-4H8a2 2 0 000 4 1 1 0 011 1z" />
                </svg>
                {isPinned ? "Unpin Message" : "Pin Message"}
              </button>
            )}

            <button
              className="context-menu-item reply"
              onClick={handleReplyClick}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="9,11 12,14 22,4" />
                <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
              </svg>
              Reply
            </button>

            {message.messageType !== "deleted" && onForward && !message.viewOnce && (
              <button
                className="context-menu-item forward"
                onClick={handleForwardClick}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="15 17 20 12 15 7" />
                  <path d="M4 18v-2a4 4 0 0 1 4-4h12" />
                </svg>
                Forward
              </button>
            )}

            {showGroupStatusInfo && (
              <button
                className="context-menu-item info"
                onClick={handleStatusClick}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                Status/Info
              </button>
            )}

            <button
              className="context-menu-item share"
              onClick={handleShareClick}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
              Share
            </button>
          </>
        )}
        </>
        )}
        </div>
      </>
    ) : null;

  return (
    <>
      {menuLayer ? createPortal(menuLayer, document.body) : null}

      <MessageInfoModal
        isVisible={showInfoModal}
        info={messageInfo}
        onClose={() => {
          setShowInfoModal(false);
          setMessageInfo(null);
          onClose();
        }}
      />
    </>
  );
};

export default MessageContextMenu;
